import { ethers } from "ethers";
import Web3 from "./web3";
import UniswapV4ReaderABI from "../abis/UniswapV4Reader.abi.json";

const protocolMap: Record<string, number> = {
    'UniswapV4': 2,
    'PancakeInfCl': 5,
}

interface ProtocolAddress {
    poolManager: string;
    positionManager: string;
}

const protocolAddressMap: Record<string, Record<string, ProtocolAddress>> = {
    'bnb': {
        'UniswapV4': {
            poolManager: '0x28e2Ea090877bF75740558f6BFB36A5ffeE9e9dF',
            positionManager: '0x7A4a5c919aE2541AeD11041A1AEeE68f1287f95b',
        },
        'PancakeInfCl': {
            poolManager: '0xa0FfB9c1CE1Fe56963B0321B32E7A0302114058b',
            positionManager: '0x0000000000000000000000000000000000000000',
        },
    },
    'eth': {
        'UniswapV4': {
            poolManager: '0x000000000004444c5dc75cB358380D2e3dE08A90',
            positionManager: '0xbD216513d74C8cf14cf4747E6AaA6420FF64ee9e',
        },
    },
    'base': {
        'UniswapV4': {
            poolManager: '0x498581fF718922c3f8e6A244956aF099B2652b2b',
            positionManager: '0x7C5f5A4bBd8fD63184577525326123B519429bDc',
        },
        'PancakeInfCl': {
            poolManager: '0xa0FfB9c1CE1Fe56963B0321B32E7A0302114058b',
            positionManager: '0x0000000000000000000000000000000000000000',
        },
    },
    'robinhood': {
        'UniswapV4': {
            poolManager: '0x8366a39CC670B4001A1121B8F6A443A643e40951',
            positionManager: '0x58daec3116aae6D93017bAAea7749052E8a04fA7',
        },
    },
}

interface TickInfo {
    tick: number;
    price: number;
    liquidityGross: bigint;
}

export interface TickArray {
    tick: number;
    price: number;
    liquidity: bigint;
    ticks: TickInfo[];
}

export default {
    uniswapV4Ticks: async function (chain: string, pool_id: string, protocol: string, tickSpacing: number): Promise<TickArray> {
        const url = Web3.getNodeUrl(chain ?? '');
        const readerAddress = Web3.getReaderAddress(chain ?? '', protocol ?? '');
        const protocolType = protocolMap[protocol] || 4;
        const provider = new ethers.JsonRpcProvider(url);
        const contract = new ethers.Contract(readerAddress, UniswapV4ReaderABI, provider);
        const protocolAddress = protocolAddressMap[chain][protocol];
        const bitmapIndexStart = -8;
        const bitmapIndexEnd = 8;
        const response = await contract.uniswapV4Ticks({
            poolManager: protocolAddress?.poolManager || ethers.ZeroAddress,
            positionManager: protocolAddress?.positionManager || ethers.ZeroAddress,
            poolId: pool_id,
            protocol: protocolType,
            bitmapOffsetStart: bitmapIndexStart,
            bitmapOffsetEnd: bitmapIndexEnd,
        });

        let tickArray: TickArray = {
            tick: Number(response.tick),
            price: Number(1.0001 ** Number(response.tick)),
            liquidity: response.liquidity,
            ticks: Array.from({ length: response.ticks.length }, () => ({
                tick: 0,
                price: 0,
                liquidityGross: BigInt(0)
            }))
        };

        let valid = 0
        for (let i = 0; i < response.ticks.length; i++) {
            if (response.ticks[i].tick >= response.tick && valid == 0) {
                valid = 1;
            }
            if (valid > 0) {
                tickArray.ticks[i].tick = Number(response.ticks[i].tick);
                if (response.ticks[i].tick == response.tick) {
                    tickArray.ticks[i].liquidityGross = BigInt(response.liquidity);
                    valid = 2;
                } else {
                    if (valid > 1) {
                        tickArray.ticks[i].liquidityGross = tickArray.ticks[i - 1].liquidityGross + BigInt(response.ticks[i].liquidityNet);
                    } else {
                        tickArray.ticks[i].liquidityGross = BigInt(response.liquidity) + BigInt(response.ticks[i].liquidityNet);
                        valid = 2;
                    }
                }
            }
        }

        valid = 0
        for (let i = response.ticks.length - 1; i >= 0; i--) {
            if (response.ticks[i].tick <= response.tick && valid == 0) {
                valid = 1;
            }
            if (valid > 0) {
                tickArray.ticks[i].tick = Number(response.ticks[i].tick);
                if (response.ticks[i].tick == response.tick) {
                    tickArray.ticks[i].liquidityGross = BigInt(response.liquidity);
                    valid = 2;
                } else {
                    if (valid > 1) {
                        tickArray.ticks[i].liquidityGross = tickArray.ticks[i + 1].liquidityGross - BigInt(response.ticks[i].liquidityNet);
                    } else {
                        tickArray.ticks[i].liquidityGross = BigInt(response.liquidity) - BigInt(response.ticks[i].liquidityNet);
                        valid = 2;
                    }
                }
            }
        }
        const tickOffset = Number(response.tick) - Number(response.tick) % 256;
        if (response.ticks.length == 0) {
            tickArray.ticks = tickArray.ticks.concat({
                tick: bitmapIndexStart * 256 * tickSpacing + tickOffset,
                price: 0,
                liquidityGross: response.liquidity,
            }, {
                tick: bitmapIndexEnd * 256 * tickSpacing + tickOffset,
                price: 0,
                liquidityGross: response.liquidity,
            });
        } else {
            tickArray.ticks = [{
                tick: bitmapIndexStart * 256 * tickSpacing + tickOffset,
                price: 0,
                liquidityGross: tickArray.ticks[0].liquidityGross,
            }, ...tickArray.ticks, {
                tick: bitmapIndexEnd * 256 * tickSpacing + tickOffset,
                price: 0,
                liquidityGross: tickArray.ticks[tickArray.ticks.length - 1].liquidityGross,
            }]
        }

        for (let i = 0; i < tickArray.ticks.length; i++) {
            tickArray.ticks[i].price = 1.0001 ** tickArray.ticks[i].tick;
        }
        return tickArray;
    },
    tickToPrice: function (tick: number, decimals0: number, decimals1: number): number {
        return (1.0001 ** tick) * (10 ** (decimals0 - decimals1));
    },
}
